'use client';

import { useMemo } from 'react';

import type {
  PersistedStepTimings,
  StepTimingsMap,
} from '@/components/assistant-ui/thread/trail-timing';
import type { LiveTrailTimingsEntry } from '@/lib/assistant-cloud-local/stores/live-trail-timings';

/**
 * Merges step timings: liveMirror → persisted → metadataStepTimings, with
 * later sources overriding earlier ones for the keys they have. Persisted
 * winning over live keeps the just-finished and post-refresh views
 * numerically identical for the same step ids.
 */
export function useMergedStepTimings({
  liveMirror,
  storeStepTimings,
  metadataStepTimings,
}: {
  liveMirror: LiveTrailTimingsEntry | undefined;
  storeStepTimings: PersistedStepTimings | undefined;
  metadataStepTimings: PersistedStepTimings | undefined;
}): StepTimingsMap {
  return useMemo<StepTimingsMap>(() => {
    const out: StepTimingsMap = {};
    if (liveMirror?.stepTimings) {
      for (const [id, timing] of Object.entries(liveMirror.stepTimings)) {
        out[id] = { startedAt: timing.startedAt, completedAt: timing.completedAt };
      }
    }
    const persisted = storeStepTimings ?? metadataStepTimings;
    if (persisted) {
      for (const [id, timing] of Object.entries(persisted)) {
        out[id] = { startedAt: timing.startedAt, completedAt: timing.completedAt };
      }
    }
    return out;
  }, [liveMirror, storeStepTimings, metadataStepTimings]);
}
